import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  FacebookFilled,
  TwitterSquareFilled,
  InstagramOutlined,
  PinterestFilled,
  YoutubeFilled,
} from "@ant-design/icons";
import { postsService, getMediaBaseUrl, Post } from "../../api/api.service";
import "./Sidebar.css";

const Sidebar: React.FC = () => {
  const [popularPosts, setPopularPosts] = useState<Post[]>([]);
  const [relatedPosts, setRelatedPosts] = useState<Post[]>([]);
  const [postImages, setPostImages] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Use a stable ref so it never triggers the useEffect again
  const fallbackImageRef = useRef(`${getMediaBaseUrl()}/wp-content/uploads/2025/10/profile-1.jpg`);

  useEffect(() => {
    let cancelled = false;
    const fetchPosts = async () => {
      setLoading(true);
      setError(false);
      try {
        const popular = await postsService.getPopularPosts(3);
        const related = await postsService.getRelatedPosts(2, 3);

        if (cancelled) return;

        setPopularPosts(popular);
        setRelatedPosts(related);

        const imageMap: Record<number, string> = {};
        [...popular, ...related].forEach((post: any) => {
          const featuredImage =
            post._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
            fallbackImageRef.current;
          imageMap[post.id] = featuredImage;
        });
        setPostImages(imageMap);
      } catch (err) {
        if (!cancelled) {
          setError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPosts();
    return () => { cancelled = true; };
  }, []); // ✅ Empty deps — fetch only once on mount, no infinite loop

  return (
    <aside className="sidebar">
      {/* Profile Box */}
      <div className="sidebar-box">
        <div className="sidebar-brand">
          Lifestyle Online<div className="sidebar-brand-dot"></div>
        </div>
        <p className="sidebar-description">
          Hello, We're content writers fascinated by fashion, celebrity and lifestyle.
          We help clients bring the right content to the right people.
        </p>
        <div className="sidebar-socials">
          {[FacebookFilled, TwitterSquareFilled, InstagramOutlined, PinterestFilled, YoutubeFilled].map(
            (Icon, i) => (
              <Icon
                key={i}
                className="sidebar-social-icon"
              />
            )
          )}
        </div>
      </div>

      {/* Popular Posts */}
      <div className="sidebar-box">
        <h3 className="sidebar-section-title">Popular Posts</h3>
        {loading && (
          <p className="sidebar-status-msg">Loading posts…</p>
        )}
        {!loading && error && (
          <p className="sidebar-status-msg sidebar-status-error">
            Unable to load posts. Please check your connection.
          </p>
        )}
        {!loading && !error && popularPosts.length === 0 && (
          <p className="sidebar-status-msg">No posts found.</p>
        )}
        {popularPosts.map((post, index) => (
          <Link key={post.id} to={`/post/${post.id}`} className="sidebar-post-item">
            <div className="sidebar-post-rank">{index + 1}</div>
            <img
              src={postImages[post.id]}
              alt={`Post ${index + 1}`}
              className="sidebar-post-thumb"
            />
            <div className="sidebar-post-content">
              <div
                className="sidebar-post-title"
                dangerouslySetInnerHTML={{ __html: post.title.rendered }}
              />
              <div className="sidebar-post-date">
                {new Date(post.date).toLocaleDateString()}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Related Blog */}
      <div className="sidebar-box">
        <h3 className="sidebar-section-title">Related Blog</h3>
        {loading && (
          <p className="sidebar-status-msg">Loading posts…</p>
        )}
        {!loading && error && (
          <p className="sidebar-status-msg sidebar-status-error">
            Unable to load posts. Please check your connection.
          </p>
        )}
        {!loading && !error && relatedPosts.length === 0 && (
          <p className="sidebar-status-msg">No posts found.</p>
        )}
        {relatedPosts.map((post) => (
          <Link key={post.id} to={`/post/${post.id}`} className="sidebar-related-item">
            <img
              src={postImages[post.id]}
              alt={`Related post ${post.id}`}
              className="sidebar-related-thumb"
            />
            <div className="sidebar-related-content">
              <div
                className="sidebar-related-title"
                dangerouslySetInnerHTML={{ __html: post.title.rendered }}
              />
            </div>
          </Link>
        ))}
      </div>
    </aside>
  );
};

export default Sidebar;
